/**
 * Centralized logging utility for the orchestrator
 */

export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

export interface LoggerConfig {
  level: LogLevel;
  prefix: string;
  enabled: boolean;
}

export class Logger {
  private config: LoggerConfig;

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: LogLevel.INFO,
      prefix: '[KaigenticChat]',
      enabled: true,
      ...config
    };
  }

  /**
   * Update logger configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Log error message
   */
  error(message: string, ...args: unknown[]): void {
    this.log(LogLevel.ERROR, message, ...args);
  }

  /**
   * Log warning message
   */
  warn(message: string, ...args: unknown[]): void {
    this.log(LogLevel.WARN, message, ...args);
  }

  /**
   * Log info message
   */
  info(message: string, ...args: unknown[]): void {
    this.log(LogLevel.INFO, message, ...args);
  }

  /**
   * Log debug message
   */
  debug(message: string, ...args: unknown[]): void {
    this.log(LogLevel.DEBUG, message, ...args);
  }

  /**
   * Log error with context
   */
  errorWithContext(message: string, error?: Error | unknown, context?: Record<string, unknown>): void {
    const errorDetails = error instanceof Error 
      ? { message: error.message, stack: error.stack, name: error.name }
      : error;
    
    this.error(message, { error: errorDetails, context });
  }

  /**
   * Internal logging method
   */
  private log(level: LogLevel, message: string, ...args: unknown[]): void {
    if (!this.config.enabled || level > this.config.level) {
      return;
    }

    const timestamp = new Date().toISOString();
    const levelName = LogLevel[level];
    const prefix = `${this.config.prefix} [${timestamp}] [${levelName}]`;
    const fullMessage = `${prefix} ${message}`;

    switch (level) {
      case LogLevel.ERROR:
        console.error(fullMessage, ...args);
        break;
      case LogLevel.WARN:
        console.warn(fullMessage, ...args);
        break;
      case LogLevel.INFO:
        console.info(fullMessage, ...args);
        break;
      case LogLevel.DEBUG:
        console.debug(fullMessage, ...args);
        break;
      default:
        console.log(fullMessage, ...args);
    }
  }

  /**
   * Create child logger with extended prefix
   */
  createChild(childPrefix: string): Logger {
    return new Logger({
      ...this.config,
      prefix: `${this.config.prefix} ${childPrefix}`
    });
  }

  /**
   * Enable or disable logger
   */
  setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
  }

  /**
   * Set log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Get current configuration
   */
  getConfig(): LoggerConfig {
    return { ...this.config };
  }
}

// Default logger instance
export const logger = new Logger();

// Convenience function to create component-specific loggers
export function createLogger(component: string, debug: boolean = false): Logger {
  return new Logger({
    level: debug ? LogLevel.DEBUG : LogLevel.INFO,
    prefix: `[KaigenticChat:${component}]`,
    enabled: true
  });
}